import React, { useState, useMemo } from "react";
import {
  Box, Typography, Card, Chip, Dialog, DialogTitle, DialogContent,
  IconButton, TextField, InputAdornment, LinearProgress, Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Navbar from "../navbar";
import { QUESTIONNAIRES, getAssessmentsByCategory } from "./questionnaires";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const categoryStyle = {
  anxiety:    { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa", label: "Anxiety" },
  depression: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", label: "Depression" },
  other:      { bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe", label: "Other" },
};

const severityColor = {
  green:  { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  yellow: { bg: "#fefce8", color: "#a16207", border: "#fef08a" },
  orange: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
  red:    { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
};

// ─── Assessment Card ───────────────────────────────────────────────────────────
const AssessmentCard = ({ qKey, q, onClick }) => {
  const cs = categoryStyle[q.category] || categoryStyle.other;
  return (
    <Card
      onClick={() => onClick(qKey, q)}
      sx={{ p: 2.25, borderRadius: "14px", border: "1px solid #e5e7eb", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", bgcolor: "#fff", cursor: "pointer", transition: "all 0.15s", "&:hover": { boxShadow: "0 4px 14px rgba(0,0,0,0.1)", borderColor: cs.border, transform: "translateY(-1px)" } }}
    >
      {/* Icon + Name */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.25 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: "12px", bgcolor: cs.bg, border: "1px solid " + cs.border, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem", flexShrink: 0 }}>
          {q.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#111827", lineHeight: 1.35 }}>{q.name}</Typography>
          <Typography sx={{ fontSize: "0.72rem", color: "#6b7280", fontWeight: 600 }}>{q.shortName}</Typography>
        </Box>
      </Box>

      {/* Description */}
      <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", lineHeight: 1.55, mb: 1.5 }}>{q.description}</Typography>

      {/* Footer chips */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
        <Chip label={cs.label} size="small" sx={{ height: 20, fontSize: "0.63rem", fontWeight: 600, bgcolor: cs.bg, color: cs.color, border: "1px solid " + cs.border, "& .MuiChip-label": { px: 0.75 } }} />
        <Chip label={q.questions.length + " questions"} size="small" sx={{ height: 20, fontSize: "0.63rem", fontWeight: 500, bgcolor: "#f1f5f9", color: "#475569", "& .MuiChip-label": { px: 0.75 } }} />
        <Chip label={"Max " + q.maxScore} size="small" sx={{ height: 20, fontSize: "0.63rem", fontWeight: 500, bgcolor: "#f1f5f9", color: "#475569", "& .MuiChip-label": { px: 0.75 } }} />
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.4 }}>
          <QuizOutlinedIcon sx={{ fontSize: "0.8rem", color: "#9ca3af" }} />
          <Typography sx={{ fontSize: "0.68rem", color: "#9ca3af" }}>View details</Typography>
        </Box>
      </Box>
    </Card>
  );
};

// ─── Question Row ─────────────────────────────────────────────────────────────
const QuestionRow = ({ q, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ border: "1px solid #e9ecef", borderRadius: "10px", overflow: "hidden" }}>
      <Box
        onClick={() => setOpen((p) => !p)}
        sx={{ px: 2, py: 1.25, display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer", bgcolor: open ? "#f8fafc" : "#fff", "&:hover": { bgcolor: "#f8fafc" } }}
      >
        <Box sx={{ width: 22, height: 22, borderRadius: "6px", bgcolor: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#4338ca" }}>{index + 1}</Typography>
        </Box>
        <Typography sx={{ flex: 1, fontSize: "0.82rem", color: "#1f2937", fontWeight: 500 }}>{q.text}</Typography>
        <IconButton size="small" sx={{ p: 0, color: "#9ca3af" }}>
          {open ? <ExpandLessIcon sx={{ fontSize: "1rem" }} /> : <ExpandMoreIcon sx={{ fontSize: "1rem" }} />}
        </IconButton>
      </Box>
      {open && (
        <Box sx={{ px: 2, pb: 1.5, pt: 0.5, bgcolor: "#fafafa", borderTop: "1px solid #f1f5f9" }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
            {q.options.map((opt, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.6, px: 1.25, py: 0.5, borderRadius: "8px", bgcolor: "#fff", border: "1px solid #e2e8f0" }}>
                <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: "#e0e7ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Typography sx={{ fontSize: "0.6rem", fontWeight: 700, color: "#4338ca" }}>{opt.score}</Typography>
                </Box>
                <Typography sx={{ fontSize: "0.75rem", color: "#374151" }}>{opt.text}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ─── Detail Dialog ─────────────────────────────────────────────────────────────
const DetailDialog = ({ open, onClose, qKey, q }) => {
  if (!q) return null;
  const cs = categoryStyle[q.category] || categoryStyle.other;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden", maxHeight: "90vh" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: 3, pt: 2.5, pb: 2, bgcolor: cs.bg, borderBottom: "1px solid " + cs.border, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{ fontSize: "2rem", lineHeight: 1 }}>{q.icon}</Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#111827" }}>{q.name}</Typography>
              <Chip label={q.shortName} size="small" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, bgcolor: cs.color, color: "#fff", "& .MuiChip-label": { px: 0.875 } }} />
            </Box>
            <Typography sx={{ fontSize: "0.78rem", color: "#6b7280", mt: 0.3 }}>{q.description}</Typography>
            <Box sx={{ display: "flex", gap: 1, mt: 0.75, flexWrap: "wrap" }}>
              <Chip label={q.questions.length + " questions"} size="small" sx={{ height: 20, fontSize: "0.63rem", bgcolor: "#fff", color: "#475569", border: "1px solid #e2e8f0", "& .MuiChip-label": { px: 0.75 } }} />
              <Chip label={"Max score: " + q.maxScore} size="small" sx={{ height: 20, fontSize: "0.63rem", bgcolor: "#fff", color: "#475569", border: "1px solid #e2e8f0", "& .MuiChip-label": { px: 0.75 } }} />
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: "#6b7280", mt: -0.5, flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Instructions */}
        {q.instructions && (
          <Box sx={{ px: 3, py: 1.75, bgcolor: "#f8fafc", borderBottom: "1px solid #f1f5f9" }}>
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.4 }}>Instructions</Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "#374151", fontStyle: "italic" }}>{q.instructions}</Typography>
          </Box>
        )}

        {/* Scoring guide */}
        <Box sx={{ px: 3, py: 1.75, borderBottom: "1px solid #f1f5f9" }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>Scoring Guide</Typography>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {q.scoring.map((s, i) => {
              const sc = severityColor[s.color] || severityColor.orange;
              return (
                <Box key={i} sx={{ px: 1.25, py: 0.75, borderRadius: "8px", bgcolor: sc.bg, border: "1px solid " + sc.border, minWidth: 110 }}>
                  <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: sc.color, textTransform: "uppercase", letterSpacing: "0.04em" }}>{s.level}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: sc.color }}>{s.min}–{s.max}</Typography>
                  <Typography sx={{ fontSize: "0.65rem", color: sc.color + "bb", mt: 0.25 }}>{s.recommendation}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Questions */}
        <Box sx={{ px: 3, py: 2 }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.25 }}>
            Questions ({q.questions.length})
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {q.questions.map((question, i) => (
              <QuestionRow key={question.id} q={question} index={i} />
            ))}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════════════════════════════════════
const Assessments = () => {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null); // { qKey, q }

  const { anxiety, depression, other } = getAssessmentsByCategory();

  const allEntries = useMemo(() => [
    ...anxiety.map(([k, v]) => ({ key: k, q: v })),
    ...depression.map(([k, v]) => ({ key: k, q: v })),
    ...other.map(([k, v]) => ({ key: k, q: v })),
  ], []);

  const filtered = useMemo(() => {
    if (!search.trim()) return allEntries;
    const qStr = search.toLowerCase();
    return allEntries.filter((e) =>
      e.q.name.toLowerCase().includes(qStr) ||
      e.q.shortName?.toLowerCase().includes(qStr) ||
      e.q.category.toLowerCase().includes(qStr) ||
      e.q.description?.toLowerCase().includes(qStr)
    );
  }, [allEntries, search]);

  const groups = [
    { label: "Anxiety", color: categoryStyle.anxiety.color, items: filtered.filter((e) => e.q.category === "anxiety") },
    { label: "Depression", color: categoryStyle.depression.color, items: filtered.filter((e) => e.q.category === "depression") },
    { label: "Other", color: categoryStyle.other.color, items: filtered.filter((e) => e.q.category === "other") },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f4f6f9" }}>
      <Navbar />
      <Box sx={{ mx: { xs: 2, md: 4 }, py: 3, maxWidth: 1400 }}>

        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#111827" }}>Assessment Library</Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "#6b7280", mt: 0.3 }}>
              {allEntries.length} validated clinical assessments — click any card to view questions and scoring
            </Typography>
          </Box>
          <TextField variant="outlined" size="small" placeholder="Search assessments..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#9ca3af", fontSize: "1rem" }} /></InputAdornment>,
              sx: { borderRadius: "10px", bgcolor: "#fff", fontSize: "0.82rem", "& fieldset": { borderColor: "#e2e8f0" }, "&:hover fieldset": { borderColor: "#cbd5e1" }, "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "1.5px" } },
            }}
            sx={{ width: 280 }} />
        </Box>

        {/* Groups */}
        {groups.map((group) => group.items.length > 0 && (
          <Box key={group.label} sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.75 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: group.color }}>{group.label}</Typography>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "#e5e7eb" }} />
              <Chip label={group.items.length} size="small" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 600, bgcolor: "#f1f5f9", color: "#475569", "& .MuiChip-label": { px: 0.875 } }} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" }, gap: 1.75 }}>
              {group.items.map((e) => (
                <AssessmentCard key={e.key} qKey={e.key} q={e.q} onClick={(k, q) => setSelected({ qKey: k, q })} />
              ))}
            </Box>
          </Box>
        ))}

        {filtered.length === 0 && (
          <Box sx={{ py: 8, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <QuizOutlinedIcon sx={{ fontSize: "2.5rem", color: "#d1d5db" }} />
            <Typography sx={{ fontSize: "0.875rem", color: "#6b7280" }}>No assessments match your search</Typography>
          </Box>
        )}
      </Box>

      {/* Detail Dialog */}
      <DetailDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        qKey={selected?.qKey}
        q={selected?.q}
      />
    </Box>
  );
};

export default Assessments;
